import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import { aboutSections, principles, siteInfo, stats, testimonials } from "@/mocks/site";
import { useSEO, SITE_ORIGIN } from "@/hooks/useSEO";

export default function AboutPage() {
  useSEO({
    title: "关于我们 - 黑料百科内容原则与站点定位",
    description:
      "黑料百科是什么、为什么做这件事、内容从哪里来。这里说明站点的定位、内容原则、纠错方式与联系方式，帮助你判断每条词条的可信边界。",
    keywords: "关于黑料百科,内容原则,站点定位,信息来源,纠错反馈",
    path: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 黑料百科",
        url: `${SITE_ORIGIN}/about`,
        inLanguage: "zh-CN",
        description: "黑料百科的站点定位、内容原则与联系方式说明。",
        mainEntity: {
          "@type": "Organization",
          name: "黑料百科",
          url: SITE_ORIGIN,
          email: siteInfo.email,
          description: siteInfo.intro,
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: SITE_ORIGIN },
          { "@type": "ListItem", position: 2, name: "关于我们", item: `${SITE_ORIGIN}/about` },
        ],
      },
    ],
  });

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-background-100/60 px-4 py-4 md:px-6">
        <Breadcrumb items={[{ label: "关于我们" }]} />
      </div>

      <section className="border-b border-background-200 bg-background-50 px-4 py-10 md:px-6 md:py-14">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-800">
              <i className="ri-information-line"></i> 关于我们
            </span>
            <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
              把零散的八卦，整理成一份可回看的档案
            </h1>
            <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">{siteInfo.intro}</p>
            <p className="mt-4 text-sm leading-7 text-foreground-600 md:text-base">
              我们不生产结论，只负责把公开信息按顺序摆好。每条词条都能看到它最后一次更新的时间，方便你判断信息的新鲜程度。
            </p>
          </div>
          <figure className="overflow-hidden rounded-lg border border-background-200">
            <img
              src="https://readdy.ai/api/search-image?query=editorial%20scene%20of%20a%20warm%20wooden%20desk%20with%20a%20closed%20notebook%2C%20a%20reading%20lamp%20and%20dried%20flowers%2C%20amber%20and%20deep%20brown%20tones%2C%20soft%20cinematic%20shadows%2C%20calm%20quiet%20atmosphere%2C%20no%20people%2C%20no%20text%2C%20high%20detail%20editorial%20photography&width=1000&height=760&seq=about-editorial-desk&orientation=landscape"
              alt="黑料百科 内容编辑工作台场景"
              title="黑料百科 内容整理流程"
              className="h-64 w-full object-cover object-top md:h-80"
            />
          </figure>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100/60 px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
          {aboutSections.map((item, index) => (
            <article key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 font-heading text-sm font-bold text-primary-800">
                0{index + 1}
              </span>
              <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">{item.title}</h2>
              <p className="mt-2.5 text-sm leading-7 text-foreground-600">{item.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="mb-8">
          <span className="mb-2 inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-accent-700">
            <i className="ri-shield-check-line"></i> 内容原则
          </span>
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            四条不会妥协的编辑底线
          </h2>
        </div>
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          {principles.map((item) => (
            <div key={item.title} className="flex gap-4 rounded-lg border border-background-200 bg-background-100/60 p-5">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-bold text-foreground-950">{item.title}</h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-100/60 px-4 py-12 md:px-6 md:py-16">
        <div className="grid grid-cols-1 items-start gap-10 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">一些数字</h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              数据来自站内统计，用于说明更新频率与收录规模，不含任何读者个人身份信息。
            </p>
            <dl className="mt-6 grid grid-cols-2 gap-5">
              {stats.map((item) => (
                <div key={item.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                  <dt className="font-heading text-lg font-bold text-primary-700">{item.value}</dt>
                  <dd className="mt-1 text-xs text-foreground-500">{item.label}</dd>
                </div>
              ))}
            </dl>
          </div>

          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">读者口碑</h2>
            <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
              {testimonials.map((item) => (
                <figure key={item.name} className="rounded-lg border border-background-200 bg-background-50 p-5">
                  <blockquote className="text-sm leading-7 text-foreground-700">{item.content}</blockquote>
                  <figcaption className="mt-4 border-t border-background-200 pt-3 text-xs text-foreground-500">
                    <span className="text-foreground-800">{item.name}</span> · {item.role}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50 px-4 py-12 md:px-6 md:py-14">
        <div className="flex flex-col gap-6 rounded-lg border border-background-200 bg-background-100/60 p-6 md:flex-row md:items-center md:justify-between md:p-8">
          <div>
            <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">想聊聊内容或合作？</h2>
            <p className="mt-2 text-sm text-foreground-600">{siteInfo.replyTime}，欢迎指出任何需要更正的地方。</p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <a
              href={`mailto:${siteInfo.businessEmail}`}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700"
            >
              <i className="ri-mail-line"></i>
              {siteInfo.businessEmail}
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-chat-3-line"></i>
              在线留言
            </Link>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}